import { Scene } from "./Scene";
import { BroadcastManagerState, BroadcastThumbnails, BroadcastUnavailableReason, Log, PageReloadMode, PrivateShowRefusedBy, PrivateShowType, StateChangeReason, VideoClipFilterImpl, } from "@dreamcam/web-sdk";
import { Logger } from "../utils/Logger";
import { Settings } from "../settings/Settings";
import { text } from "../TextResources";
import { UserType } from "../interfaces/BroadcastInterfaces";
import { MessageType } from "../interfaces/NotificationCenter";
import { AppView } from "../babylon/AppView";
import { StringUtils } from "../babylon/utils/StringUtils";
import { globalOptions } from "../settings/GlobalOptions";
import { PlayerErrors, PlayerState } from "@dreamcam/web-player-main-thread";
import { waitFor } from "../utils/AsyncUtils";
import { FeatureManager, FeatureName, } from "../settings/Features";
// import {VoiceToTextSDK, VoiceToTextSDKParams} from "voice-to-text-sdk/dist"; //TODO: restore v2t
var BroadcastSwitcherState;
(function (BroadcastSwitcherState) {
    BroadcastSwitcherState[BroadcastSwitcherState["initializing"] = 0] = "initializing";
    BroadcastSwitcherState[BroadcastSwitcherState["available"] = 1] = "available";
    BroadcastSwitcherState[BroadcastSwitcherState["requesting"] = 2] = "requesting";
})(BroadcastSwitcherState || (BroadcastSwitcherState = {}));
export class BroadcastScene extends Scene {
    constructor(appController) {
        super(appController);
        this._model = null;
        this._broadcastId = null;
        this._broadcast = null;
        this._player = null;
        // private _playerUrl: Nullable<string> = null;
        // private audioSender?: AudioSender;
        // private _notificationService?: NotificationService;
        // private _broadcastManager?: BroadcastManager;
        this._broadcastSwitcherState = BroadcastSwitcherState.initializing;
        // private readonly _drawnMessages = new DrawnMessages();
        this._bmPreviousState = BroadcastManagerState.UNINITIALIZED;
        this._bmPreviousIsGuest = true;
        this._bmPreviousStateChangeReason = StateChangeReason.NONE;
        this._chatHistory = [];
        this._isCurrentPrivateAudio = false;
        this._watching = false;
        this._frequentStallsHandled = false;
        // private _v2tSdk?: VoiceToTextSDK; //TODO: restore v2t
        this._authListeners = [];
        this._nsListeners = [];
        this._bmListeners = [];
        this._chatListeners = [];
        this._clientProfileListeners = [];
        this._videoClipsTotalCount = 0;
        this._startingSpyShow = false;
        this._isReady = false;
        this._subscribed = false;
        this._relatedBroadcastThumbnails = null;
        this._loginChanged = async () => {
            if (!this.isVisible)
                return Promise.resolve();
            await this._updateLoginInfo();
        };
        this._tryToRestorePrivateShowAsync = async (data) => {
            if (!this.isVisible || this.isActivePrivateShow || this.isActiveSpyMode || !data.existingShow)
                return Promise.resolve();
            this._changeIsCurrentPrivateAudio(data.existingShow.withClientAudioRequest);
            this.appController.sdk.galleryManager.closeVideoClip();
            this.view?.outputVideoClip(null);
            this.appController.scenesSwitcher.setLobbyPageParams({ lobbyMode: "Live" });
            const openBroadcastParams = { existingPrivateShow: data.existingShow };
            this.appController.scenesSwitcher.setBroadcastPageParams(openBroadcastParams);
            await this.appController.scenesSwitcher.openBroadcastAsync(openBroadcastParams);
        };
        this._preparingToStart = false;
        this._restoreChat = () => {
            this.view?.clearChat();
            this._chatHistory.forEach((item) => {
                if (item.message) {
                    this.view?.addMessageToChat(item.message);
                }
                if (item.tip) {
                    this.view?.tipHasBeenSent(item.tip);
                }
                if (item.tipMenuItemPurchased) {
                    this.view?.tipMenuItemHasBeenRequested(item.tipMenuItemPurchased);
                }
                if (item.toysMenuItemPurchased) {
                    this.view?.toysMenuItemPurchased(item.toysMenuItemPurchased);
                }
            });
        };
        this._initView = async (broadcast, videoClip) => {
            if (!this.isVisible)
                return;
            // console.log("%c++++ videoClip:", "background-color: lime", videoClip);
            this.appController.sdk.broadcastManager.preferredStreamType = "video3D";
            if (videoClip)
                this.view?.outputVideoClip(videoClip);
            if (broadcast) {
                await this.view?.outputModelAsync(broadcast.modelId, broadcast.modelNickname);
                this.view?.setModelProfilePhoto(broadcast.modelProfilePhotoUrl);
            }
            else if (videoClip) {
                await this.view?.outputModelAsync(videoClip.modelId, videoClip.modelNickname);
                this.view?.setModelProfilePhoto(videoClip.coverImageUrl ?? "");
            }
            this.view?.outputIsFavorite(broadcast?.modelId ?? null, broadcast?.isFavorite === true);
            this.view?.outputModelTextStatus(broadcast?.broadcastTextStatus ?? "");
            if (broadcast) {
                this.view?.outputTipMenu(broadcast.tipMenuEnabled, broadcast.tipMenu);
                this.view?.outputToysMenu(broadcast.toysMenu ?? undefined);
            }
            this.view?.outputClientInfo(this._isGuest);
            this.view?.outputClientNickname(this._getNickname());
            this._restoreChat(); // (NOTE: must be after outputClientNickname call)
            // this.view?.outputPrivateModeState(this._currentPrivateModeState);
            if (broadcast) {
                this.view?.outputPrivateShowTokensPerMin(broadcast.privateModeTariff ?? 0);
                this.view?.outputPrivateShowMinutesMin(broadcast.privateModeMinDuration ?? 0);
                this.view?.outputAudioPrivateShowTokensPerMin(broadcast.privateAudioModeTariff ?? 0);
                this.view?.outputAudioPrivateShowMinutesMin(broadcast.privateAudioModeMinDuration ?? 0);
                this.view?.outputAudioPrivateShowEnabled(broadcast.withClientAudio);
                this.view?.outputSpyModeTokensPerMin(broadcast.spyModeTariff);
                this.view?.outputSpyModeEnabled(broadcast.spyModeEnabled);
                this.view?.outputIsCurrentPrivateAudio(this._isCurrentPrivateAudio);
                this.view?.outputViewerCount(broadcast.broadcastMembersCount);
                this.view?.outputLastBroadcast(broadcast.lastBroadcastDate);
            }
            this.view?.stateChanged(this._bmPreviousState, this._bmPreviousIsGuest, this._bmPreviousStateChangeReason);
            this.view?.showMenu();
            this._relatedBroadcastThumbnails?.pageItems.forEach((item) => {
                this.view?.outputRelatedBroadcastUpdated(item);
            });
            await this._readAndOutputClientBalance();
            try {
                const videoClips = await this._loadVideosAsync(0, 1);
                this._videoClipsTotalCount = videoClips.totalCount;
            }
            catch {
                this._videoClipsTotalCount = this._videoClipsTotalCount === 0 ? 1 : 0;
            }
            if (videoClip) {
                const videoClips = await this._loadRecommendedVideosAsync(videoClip);
                this.view?.outputRecommendedVideoClips(videoClips);
            }
            this.view?.outputActions(this.appController.sdk.broadcastManager.actionsState);
            this.view?.outputAvailableStreams(this.appController.sdk.broadcastManager.availableStreams ?? [], this.appController.sdk.broadcastManager.broadcast?.streams ?? []);
            this.view?.outputCurrentStream(this.appController.sdk.broadcastManager.currentStream);
            const profile = await this.appController.sdk.modelProfileManager.getModelProfileAsync();
            const modelId = broadcast?.modelId ?? (videoClip?.modelId ?? null);
            if (profile && modelId) {
                this.view?.outputModelProfile(modelId, profile, this.appController.sdk.catalogManager.filter, this._videoClipsTotalCount > 0);
            }
            const posts = await this.appController.sdk.modelProfileManager.getModelPostsAsync(5, 0);
            if (posts !== null) {
                this.view?.outputModelPosts(posts.posts);
            }
            // this.resume();
        };
        this._readAndOutputClientBalance = async () => {
            if (this._isGuest) {
                this.view?.outputClientBalance(0);
            }
            else {
                let balance;
                try {
                    balance = await this.appController.sdk.clientProfileManager.getBalanceAsync();
                }
                catch (e) {
                    console.error(e);
                    balance = 0;
                }
                if (!this.isVisible)
                    return;
                this.view?.outputClientBalance(balance);
            }
        };
        this._deleteAllUserMessages = (userId) => {
            if (!this.isVisible)
                return;
            let i = 0;
            let shouldChatBeUpdated = false;
            while (i < this._chatHistory.length) {
                const chatItem = this._chatHistory[i];
                if (chatItem.message && chatItem.message.userId === userId) {
                    this._chatHistory.splice(i, 1);
                    shouldChatBeUpdated = true;
                    continue;
                }
                i++;
            }
            if (shouldChatBeUpdated) {
                this._restoreChat();
            }
        };
        // private async _reloadBroadcastAsync(params: {
        //     broadcastId?: string,
        //     existingPrivateShow?: ExistingPrivateShow
        // }): Promise<Broadcast> {
        //     if (!params.broadcastId) {
        //         throw new Error("Object was disposed");
        //     }
        //     // const broadcast = await this.appController.dataProvider.reloadBroadcastAsync(params.broadcastId, true);
        //     const broadcast = this._broadcastManager!.broadcast;
        //     this._model = { id: broadcast!.modelId, nickname: broadcast!.modelNickname };
        //     return broadcast!;
        // }
        this._leavePrivateShow = async () => {
            if (this.appController.sdk.broadcastManager.state !== BroadcastManagerState.MY_PRIVATE_SHOW) {
                return;
            }
            await this._stopWatchingLiveStreamAsync();
            if (!this.appController.sdk.broadcastManager) {
                return;
            }
            try {
                await this.appController.sdk.broadcastManager.cancelPrivateShowAsync();
            }
            catch (error) {
            }
            // if (this.audioSender) {
            //     this.audioSender.stop();
            // }
            // this.hasPrivateShowBeenRequested = false;
            this.appController.notificationCenter?.showMessage(MessageType.info, text.broadcast_menu_privateShow_privateShowStopped);
        };
        // private readonly _detectPrivateModeState = (): PrivateModeState => {
        //     if (this.appController.sdk.broadcastManager.isGuest) {
        //         return this.appController.sdk.broadcastManager.state === State.PRIVATE_SHOW
        //             ? PrivateModeState.spyModeInfo
        //             : PrivateModeState.privateShowInfo;
        //     }
        //     switch (this.appController.sdk.broadcastManager.state) {
        //         case State.OFFLINE:
        //             return PrivateModeState.privateShowInfo;
        //         case State.MY_PRIVATE_SHOW:
        //             return PrivateModeState.privateShowLeave;
        //         case State.SPY_SHOW:
        //             return PrivateModeState.spyModeLeave;
        //         case State.PRIVATE_SHOW:
        //             return PrivateModeState.spyModeInfo;
        //         default:
        //             return this._hasPrivateModeRequest
        //                 ? PrivateModeState.privateShowRequested : PrivateModeState.privateShowInfo;
        //     }
        // }
        this._onBroadcastManagerStreamsChanged = (args) => {
            if (this.appController.sdk.broadcastManager.state === BroadcastManagerState.UNINITIALIZED)
                return;
            // if (this.appController.scenesSwitcher.getSitePageParams().broadcastPageParams === undefined) return;
            this.view?.outputAvailableStreams(args.availableStreams ?? [], args.streams ?? []);
        };
        this._inBroadcastManagerCurrentStreamChangedHandler = false;
        this._onBroadcastManagerCurrentStreamChanged = async (args) => {
            // console.log("%c+++++++++ CurrentStreamChanged args:", "background-color: coral", args);
            if (this.appController.sdk.galleryManager.videoClip !== null)
                return;
            // await waitFor(() => !this._inBroadcastManagerCurrentStreamChangedHandler, 10_000);
            await waitFor(() => !this._inBroadcastManagerCurrentStreamChangedHandler, 15_000, { logErrorOnTimeout: false, rejectOnTimeout: false });
            this._inBroadcastManagerCurrentStreamChangedHandler = true;
            try {
                this._frequentStallsHandled = false;
                if (this.appController.sdk.broadcastManager.state === BroadcastManagerState.UNINITIALIZED) {
                    // if (this.appController.scenesSwitcher.getSitePageParams().broadcastPageParams === undefined) {
                    await this._stopWatchingLiveStreamAsync();
                }
                else {
                    this.view?.outputCurrentStream(args.streamInfo);
                    if (args.streamInfo === null || args.streamInfo.status === "offline") {
                        await this._stopWatchingLiveStreamAsync();
                    }
                    if (args.streamInfo !== null && args.streamInfo.status === "online") {
                        await this._startWatchingLiveStreamAsync(args.streamInfo);
                    }
                }
            }
            finally {
                this._inBroadcastManagerCurrentStreamChangedHandler = false;
            }
        };
        this._onBroadcastManagerStateChanged = async (data) => {
            const state = data.state;
            const isGuest = data.isGuest;
            const reason = data.reason;
            const broadcast = this.appController.sdk.broadcastManager.broadcast;
            if (this._bmPreviousState === BroadcastManagerState.OFFLINE && state !== BroadcastManagerState.OFFLINE) {
                // On leaving Offline state:
                this.view?.outputPrivateShowTokensPerMin(broadcast.privateModeTariff ?? 0);
                this.view?.outputPrivateShowMinutesMin(broadcast.privateModeMinDuration ?? 0);
                this.view?.outputAudioPrivateShowTokensPerMin(broadcast.privateAudioModeTariff ?? 0);
                this.view?.outputAudioPrivateShowMinutesMin(broadcast.privateAudioModeMinDuration ?? 0);
                this.view?.outputAudioPrivateShowEnabled(broadcast.withClientAudio);
                this.view?.outputSpyModeTokensPerMin(broadcast.spyModeTariff);
                this.view?.outputSpyModeEnabled(broadcast.spyModeEnabled);
                this.view?.outputModelTextStatus(broadcast.broadcastTextStatus);
            }
            else if (this._bmPreviousState !== BroadcastManagerState.OFFLINE && state === BroadcastManagerState.OFFLINE) {
                // On entering Offline state:
                this.view?.outputModelTextStatus("");
            }
            this._changeIsCurrentPrivateAudio(this.appController.sdk.broadcastManager.currentPrivateShowType == PrivateShowType.Voice);
            // if (state === State.OFFLINE || state === State.MY_PRIVATE_SHOW || state === State.SPY_SHOW)
            //     this._hasPrivateModeRequest = false;
            // Notification:
            if (state !== this._bmPreviousState) {
                const modelNickname = this.appController.sdk.broadcastManager.broadcast.modelNickname;
                if (state === BroadcastManagerState.MY_PRIVATE_SHOW || state === BroadcastManagerState.SPY_SHOW) {
                    const notifyOnStartShow = () => {
                        const notificationMessage = state === BroadcastManagerState.MY_PRIVATE_SHOW
                            ? text.broadcast_menu_privateShow_privateShowStarted
                            : text.broadcast_menu_spyMode_spyModeStarted;
                        this.appController.notificationCenter?.showMessage(MessageType.info, notificationMessage);
                    };
                    if (this.appController.sdk.galleryManager.videoClip !== null) {
                        await this._switchToPrivateOrSpyShowAsync(modelNickname);
                    }
                    else {
                        // await this._startWatchingLiveStreamAsync(this.appController.sdk.broadcastManager.currentStream, false);
                    }
                    notifyOnStartShow();
                    this._bmPreviousState = BroadcastManagerState.UNINITIALIZED;
                }
                if (this._bmPreviousState === BroadcastManagerState.SPY_SHOW) {
                    if (reason === StateChangeReason.SPY_SHOW_DISABLED_BY_BILLING)
                        this.appController.notificationCenter?.showMessage(MessageType.warning, text.broadcast_menu_spyMode_spyModeStoppedByBilling);
                    else if (reason === StateChangeReason.SPY_SHOW_CANCELED_DUE_TO_TECHNICAL_REASONS)
                        this.appController.notificationCenter?.showMessage(MessageType.error, text.broadcast_menu_spyMode_spyModeStoppedByTechReasons);
                    else
                        this.appController.notificationCenter?.showMessage(MessageType.info, StringUtils.stringFormat(text.broadcast_menu_spyMode_spyModeStopped, modelNickname));
                }
            }
            switch (data.reason) {
                case StateChangeReason.PRIVATE_SHOW_DISABLED_BY_MODEL:
                    this.appController.notificationCenter?.showMessage(MessageType.info, text.broadcast_menu_privateShow_privateShowStoppedByModel);
                    break;
                case StateChangeReason.PRIVATE_SHOW_DISABLED_BY_BILLING:
                    this.appController.notificationCenter?.showMessage(MessageType.info, text.broadcast_menu_privateShow_privateShowStoppedByBilling);
                    break;
                case StateChangeReason.PRIVATE_SHOW_CANCELED_DUE_TO_TECHNICAL_REASONS:
                    this.appController.notificationCenter?.showMessage(MessageType.error, text.broadcast_menu_privateShow_privateShowCanceledDueToTechnicalReasons);
                    break;
            }
            // const privateModeState: PrivateModeState = this._detectPrivateModeState();
            // this._changePrivateModeState(privateModeState);
            if (data.state !== BroadcastManagerState.UNINITIALIZED) {
                this.view?.outputIsFavorite(broadcast?.modelId ?? null, broadcast.isFavorite === true);
            }
            this._bmPreviousState = state;
            this._bmPreviousIsGuest = isGuest;
            this._bmPreviousStateChangeReason = reason;
            this.view?.stateChanged(state, isGuest, reason);
        };
        this._clearChat = () => {
            // this._drawnMessages.clear();
            this.view?.clearChat();
            this._chatHistory.length = 0;
        };
        this._addChatItem = (item) => {
            this._chatHistory.push(item);
            while (this._chatHistory.length > BroadcastScene.MAX_CHAT_HISTORY) {
                this._chatHistory.shift();
            }
        };
        this._updateLoginInfo = async () => {
            this.view?.outputClientInfo(this._isGuest);
            this.view?.outputClientNickname(this._getNickname());
            this.view?.outputIsFavorite(this._broadcast?.modelId ?? null, this._broadcast?.isFavorite === true);
            if (this._relatedBroadcastThumbnails) {
                await this._relatedBroadcastThumbnails.tryReloadBroadcastsAsync();
            }
            await this._readAndOutputClientBalance();
            const galleryManager = this.appController.sdk.galleryManager;
            if (galleryManager.videoClip) {
                try {
                    await galleryManager.openVideoClipAsync(galleryManager.videoClip.id, { loadStreamData: true });
                    this.view?.outputVideoClip(galleryManager.videoClip);
                }
                catch (e1) {
                    if (e1.code === 404 && this.appController.sdk.authManager.isAuthorized && galleryManager.videoClip?.product?.productId) {
                        try {
                            await galleryManager.openVideoClipPurchaseAsync(galleryManager.videoClip.product.productId, { loadStreamData: true });
                            this.view?.outputVideoClip(galleryManager.videoClip);
                        }
                        catch (e2) {
                            galleryManager.closeVideoClip();
                            this.view?.outputVideoClip(null);
                        }
                    }
                    else {
                        galleryManager.closeVideoClip();
                        this.view?.outputVideoClip(null);
                    }
                }
            }
        };
        this._openLobbyAsync = async (filter) => {
            if (this._preparingToStart)
                return;
            await this._stopPrivateOrSpyMode();
            let params = this.appController.scenesSwitcher.getSitePageParams().lobbyPageParams;
            if (params === undefined)
                params = { lobbyMode: "Live", lobbyVideosMode: "AllVideos" };
            if (filter !== undefined) {
                this.appController.sdk.catalogManager.filter.setFilter({
                    categoryCodes: filter.categoryCodes,
                    tagCodes: filter.tagCodes,
                    streamTypeCodes: filter.streamCodes,
                    favoritesOnly: this.appController.sdk.catalogManager.filter.favoritesOnly,
                });
                params.lobbyMode = "Live";
            }
            //TODO DZZ DCVR-5832, DCVR-5831 Fix SDK and remove this line!
            // @ts-ignore
            this.appController.sdk.nsManager.leaveRoomChat();
            this.appController.sdk.galleryManager.closeVideoClip();
            // this.view?.outputVideoClip(null);
            await this.appController.scenesSwitcher.openLobbyAsync(params);
        };
        this._openBroadcastAsync = async (params) => {
            if (!this.isVisible || !this._isReady || (params.broadcastId === undefined && params.modelNickname === undefined)) {
                return;
            }
            this.appController.scenesSwitcher.setBroadcastPageParams(params);
            await this.appController.scenesSwitcher.openBroadcastAsync(params);
            // if (this._thumbnails !== null) {
            //     this._thumbnails.dispose();
            //     this._thumbnails = null;
            // }
        };
        this._viewCallbacks = {
            isAuthorized: () => {
                return this.appController.sdk.authManager.isAuthorized;
            },
            sendMessageAsync: async (message) => {
                if (message !== "") {
                    const roomChatId = this.appController.sdk.broadcastManager.currentRoomChatId;
                    if (roomChatId) {
                        try {
                            await this.appController.sdk.chatManager.sendMessageAsync(message);
                        }
                        catch (e) {
                            if (e.code === 406) {
                                this.appController.notificationCenter?.showMessage(MessageType.info, text.broadcast_userIsMuted);
                            }
                            else {
                                throw e;
                            }
                        }
                    }
                }
            },
            tipAsync: async (amount) => {
                if (this._model === null) {
                    throw new Error("Unknown _model");
                }
                if (amount > 0) {
                    try {
                        await this.appController.sdk.chatManager.sendTipsAsync(this._model.id, amount);
                    }
                    catch (e) {
                        this.appController.notificationCenter?.showMessage(MessageType.error, e.message);
                    }
                }
            },
            complimentAsync: async (privateRequestId, amount) => {
                try {
                    if (amount > 0) {
                        await this.appController.sdk.broadcastManager.sendComplimentAsync(privateRequestId, amount);
                        this.appController.gtm.complimentSent(amount);
                        return true;
                    }
                    return false;
                }
                catch (e) {
                    return false;
                }
            },
            buyToysMenuItemAsync: async (toyMenuItemId, amount) => {
                if (this._model === null) {
                    throw new Error("Unknown _model");
                }
                if (this._model.id && toyMenuItemId) {
                    try {
                        await this.appController.sdk.chatManager.buyToysMenuItemAsync(this._model.id, toyMenuItemId, amount);
                    }
                    catch (e) {
                        this.appController.notificationCenter?.showMessage(MessageType.error, e.message);
                    }
                }
            },
            sendTipMenuItemRequestAsync: async (tipMenuItemId) => {
                if (this._model === null) {
                    throw new Error("Unknown _model");
                }
                if (this._model.id && tipMenuItemId) {
                    try {
                        await this.appController.sdk.chatManager.sendTipMenuItemRequestAsync(this._model.id, tipMenuItemId);
                    }
                    catch (e) {
                        this.appController.notificationCenter?.showMessage(MessageType.error, e.message);
                    }
                }
            },
            requestPrivateShowAsync: async () => {
                try {
                    await this.appController.sdk.broadcastManager.requestPrivateShowAsync();
                }
                catch (e) {
                    if (e.code === 503) {
                        this.view?.showMessagePrivateShowRequestRejected(PrivateShowRefusedBy.MAINTENANCE);
                        return Promise.resolve();
                    }
                    throw e;
                }
                // this._hasPrivateModeRequest = true;
                // this._changePrivateModeState(this._detectPrivateModeState());
            },
            requestAudioPrivateShowAsync: async () => {
                try {
                    await this.appController.sdk.broadcastManager.requestVoicePrivateShowAsync();
                }
                catch (e) {
                    if (e.code === 503) {
                        this.view?.showMessagePrivateShowRequestRejected(PrivateShowRefusedBy.MAINTENANCE);
                    }
                    throw e;
                }
                // this._hasPrivateModeRequest = true;
                // this._changePrivateModeState(this._detectPrivateModeState());
            },
            cancelPrivateShowRequestAsync: async () => {
                try {
                    await this.appController.sdk.broadcastManager.cancelPrivateShowAsync();
                }
                finally {
                    // this._hasPrivateModeRequest = false;
                    // this._changePrivateModeState(this._detectPrivateModeState());
                }
            },
            leavePrivateShowAsync: async () => {
                await this._leavePrivateShow();
                // this._changePrivateModeState(this._detectPrivateModeState());
            },
            enterSpyModeAsync: async () => {
                if (this._startingSpyShow)
                    return;
                this._startingSpyShow = true;
                try {
                    if (this._isGuest) {
                        this.appController.notificationCenter?.showMessage(MessageType.info, text.broadcast_menu_warning_nonAuthorizedUser);
                    }
                    else {
                        try {
                            await this.appController.sdk.broadcastManager.requestSpyShowAsync();
                        }
                        catch (e) {
                            if (e.code === 409) {
                                this.appController.notificationCenter?.showMessage(MessageType.warning, e.message);
                            }
                            else {
                                this.appController.notificationCenter?.showMessage(MessageType.error, e.message);
                            }
                        }
                    }
                }
                finally {
                    this._startingSpyShow = false;
                }
            },
            leaveSpyModeAsync: async () => {
                try {
                    await this.appController.sdk.broadcastManager.cancelSpyShowAsync();
                }
                finally {
                    // this._changePrivateModeState(this._detectPrivateModeState());
                }
            },
            // startAudio: (): void => {
            //     if (!this._broadcastManager || !this.audioSender) {
            //         return;
            //     }
            //     const url = this._broadcastManager.privateAudioStreamUrl;
            //     if (!url) {
            //         Logger.error("URL is not defined.");
            //         return;
            //     }
            //     this.audioSender.start(url);
            //     this.setAudioControlsVisibility(AudioStatus.CONNECTED);
            // },
            //
            // stopAudio: (): void => {
            //     if (this.audioSender) {
            //         this.audioSender.stop();
            //     }
            // }
            loadPreviousBroadcast: () => {
                this._loadNeighborBroadcast("previous");
            },
            loadNextBroadcast: () => {
                this._loadNeighborBroadcast("next");
            },
            openBroadcastByIdAsync: async (broadcastId) => {
                await this._openBroadcastByIdAsync(broadcastId);
            },
            getPlayerSoundMuted() {
                return Settings.getInstance().broadcast.isSoundMuted;
            },
            setPlayerSoundMuted: (isMuted) => {
                Settings.getInstance().broadcast.isSoundMuted = isMuted;
                if (this.view) {
                    this.view.videoElement.muted = isMuted;
                }
            },
            getPlayerSoundVolume() {
                return Settings.getInstance().broadcast.soundVolume;
            },
            setPlayerSoundVolume: (volume) => {
                Settings.getInstance().broadcast.soundVolume = volume;
                if (this.view) {
                    const clamp01 = (value) => Math.max(Math.min(value, 1), 0);
                    // Use non-linear function:
                    this.view.videoElement.volume = clamp01(volume * volume);
                }
                // if (this._viewCallbacks.getPlayerSoundMuted() && volume > 0.000001) {
                //     this._viewCallbacks.setPlayerSoundMuted(false);
                // }
            },
            getPlayer: () => {
                return this._player;
            },
            getPassthroughToken: () => {
                return this.appController.sdk.broadcastManager.passthroughToken?.token ?? "";
            },
            openLobbyAsync: async (filter) => {
                await this._openLobbyAsync(filter);
            },
            openBroadcastAsync: async (params) => {
                await this._openBroadcastAsync(params);
            },
            setGalleryModel: (studioModel) => {
                const filter = this.appController.sdk.galleryManager.filter;
                filter.studioModel = studioModel;
                filter.modelId = studioModel === null ? null : studioModel.modelId;
            },
            getModelPostsAsync: async (limit, offset) => {
                const posts = await this.appController.sdk.modelProfileManager.getModelPostsAsync(limit, offset);
                if (posts !== null)
                    this.view?.outputModelPosts(posts.posts);
            },
            addModelToFavoritesAsync: async (modelId) => {
                await this.appController.sdk.clientProfileManager.favorites.addModelAsync(modelId);
            },
            removeModelFromFavoritesAsync: async (modelId) => {
                await this.appController.sdk.clientProfileManager.favorites.removeModelAsync(modelId);
            },
            purchaseVideoClipAsync: async (productId) => {
                return await this.appController.sdk.galleryManager.purchaseVideoClipAsync(productId);
                // // @ts-ignore
                // (this.appController.sdk.galleryManager.videoClip!.product! as any).isPurchased = true;
                // return {
                //     purchaseDate: new Date().getTime() / 1000,
                //     purchasePrice: 4,
                //     item: this.appController.sdk.galleryManager.videoClip!,
                // };
            },
            onPurchasedVideoClip: async (videoClip) => {
                videoClip = await this.appController.sdk.galleryManager.openVideoClipAsync(videoClip.id, { loadStreamData: true });
                this.view.outputVideoClip(videoClip);
            },
            recenter: () => {
                if (AppView.getInstance().isEmulation)
                    return;
                AppView.getInstance().recenter();
            },
            setPreferredStreamType: (preferredStreamType) => {
                this.appController.sdk.broadcastManager.preferredStreamType = preferredStreamType;
            },
            notifyViewsOnIsFavoriteChanged: (modelId, isFavorite) => {
                this.appController.messengerCore.overwriteIsFavorite(modelId, isFavorite);
                this.view?.outputIsFavorite(modelId, isFavorite);
            },
            getRelatedBroadcastsPageSize() {
                return BroadcastScene.RELATED_BROADCASTS_PAGE_SIZE;
            },
            //TODO: restore v2t
            // initVoiceToTextSDKAsync: async (onResult: (message: string) => void, onAudioChunk: (data: Float32Array) => void) => {
            //     return await this._initVoiceToTextSDKAsync(onResult, onAudioChunk);
            // },
            //
            // connectVoiceToTextSDK: () => {
            //     return this._v2tSdk?.connect() ?? false;
            // },
            //
            // disconnectVoiceToTextSDK: async (delay: number) => {
            //     return (await this._v2tSdk?.disconnectAsync(delay)) ?? false;
            // }
        };
        this._loadNeighborBroadcast = (direction) => {
            if (this._preparingToStart)
                return;
            if (this._broadcastSwitcherState !== BroadcastSwitcherState.available)
                return;
            (async () => {
                this._broadcastSwitcherState = BroadcastSwitcherState.requesting;
                try {
                    const currentBroadcastId = this._broadcastId;
                    await this._getNeighborBroadcast(this._broadcastId, direction);
                    if (this.appController.sdk.broadcastManager.broadcast && this._broadcastSwitcherState === BroadcastSwitcherState.requesting) {
                        const neighborBroadcastId = this.appController.sdk.broadcastManager.broadcast.id;
                        if (neighborBroadcastId !== currentBroadcastId) {
                            const neighborModelNickname = this.appController.sdk.broadcastManager.broadcast.modelNickname;
                            await this._changeBroadcastProcedure(neighborBroadcastId, neighborModelNickname);
                        }
                    }
                }
                finally {
                    if (this._broadcastSwitcherState === BroadcastSwitcherState.requesting) {
                        this._broadcastSwitcherState = BroadcastSwitcherState.available;
                    }
                }
            })();
        };
        // private readonly _changePrivateModeState = (privateModeState: PrivateModeState): void => {
        //     this._currentPrivateModeState = privateModeState;
        //     this.view?.outputPrivateModeState(privateModeState);
        // }
        this._changeIsCurrentPrivateAudio = (isCurrentPrivateAudio) => {
            this._isCurrentPrivateAudio = isCurrentPrivateAudio;
            this.view?.outputIsCurrentPrivateAudio(isCurrentPrivateAudio);
        };
        // this._subscribeAll();
        // this._subscribe(); //TODO: check if necessary
        // setTimeout(() => {
        //     ((this._notificationService as any).ws as WebSocket).close();
        // }, 5000);
    }
    _subscribeAuthManager() {
        this._authListeners.push(this.appController.sdk.authManager.registerListener("loggedIn", this._loginChanged));
        this._authListeners.push(this.appController.sdk.authManager.registerListener("loggedOut", this._loginChanged));
        this._authListeners.push(this.appController.sdk.authManager.registerListener("incompleteSessionFound", this._tryToRestorePrivateShowAsync));
    }
    _subscribeNsManager() {
        this._nsListeners.push(this.appController.sdk.nsManager.registerListener("onError", (data) => {
            Logger.error(`Notification service error: ${JSON.stringify(data)}`);
        }));
    }
    _subscribeBroadcastManager() {
        this._bmListeners.push(this.appController.sdk.broadcastManager.registerListener("onStateChanged", async (data) => this._onBroadcastManagerStateChanged(data)), this.appController.sdk.broadcastManager.registerListener("onStreamsChanged", this._onBroadcastManagerStreamsChanged), this.appController.sdk.broadcastManager.registerListener("onCurrentStreamChanged", this._onBroadcastManagerCurrentStreamChanged), this.appController.sdk.broadcastManager.registerListener("onActionsChanged", (_data) => {
        }), this.appController.sdk.broadcastManager.registerListener("onPassthroughTokenChanged", (data) => {
            if (this._player)
                this._player.passthroughToken = data.passthroughToken?.token ?? "";
        }), this.appController.sdk.broadcastManager.registerListener("onToysMenuChanged", (data) => {
            this.view?.outputToysMenu(data.toysMenu ?? undefined);
        }), this.appController.sdk.broadcastManager.registerListener("onPrivateShowRequestRefused", (args) => {
            Logger.debug(`Private mode request is rejected by ${PrivateShowRefusedBy[args.reason].toLowerCase()}`);
            // this._hasPrivateModeRequest = false;
            // this._changePrivateModeState(this._detectPrivateModeState());
            this.view?.showMessagePrivateShowRequestRejected(args.reason);
        }), this.appController.sdk.broadcastManager.registerListener("onPrivateShowRequestExpired", () => {
            Logger.error(`Private mode request is expired`);
            // this._hasPrivateModeRequest = false;
            // this._changePrivateModeState(this._detectPrivateModeState());
        }), this.appController.sdk.broadcastManager.registerListener("onBroadcastUnavailable", async (args) => {
            // console.log(BroadcastUnavailableReason[reasonType], "=>", reasonText);
            if (args.reason === BroadcastUnavailableReason.BANNED) {
                const modelNickname = args.modelNickname ?? "Model";
                const durationText = (() => {
                    switch (args.duration) {
                        case "hour": return "1 hour";
                        case "day": return "24 hours";
                        case "week": return "1 week";
                        default: return undefined;
                    }
                })();
                const messageText = durationText === undefined
                    ? StringUtils.stringFormat(text.broadcast_broadcastPermanentlyBlocked, modelNickname)
                    : StringUtils.stringFormat(text.broadcast_broadcastTemporarilyBlocked, modelNickname, durationText);
                this.appController.notificationCenter?.showMessage(MessageType.warning, messageText);
                // } else {
                //     this.appController.notificationCenter?.showMessage(MessageType.warning, text.broadcast.broadcastNotFound);
            }
            this.view?.closeMessenger();
            await this._openLobbyAsync();
        }), this.appController.sdk.broadcastManager.registerListener("onBroadcastSettingsChanged", (data) => {
            this.view?.hidePrivateShowRequest();
            if (data.privateShowTariff !== undefined)
                this.view?.outputPrivateShowTokensPerMin(data.privateShowTariff);
            if (data.privateShowMinDuration !== undefined)
                this.view?.outputPrivateShowMinutesMin(data.privateShowMinDuration);
            if (data.voicePrivateShowTariff !== undefined)
                this.view?.outputAudioPrivateShowTokensPerMin(data.voicePrivateShowTariff);
            if (data.voicePrivateShowMinDuration !== undefined)
                this.view?.outputAudioPrivateShowMinutesMin(data.voicePrivateShowMinDuration);
            if (data.withClientVoice !== undefined)
                this.view?.outputAudioPrivateShowEnabled(data.withClientVoice);
            if (data.spyShowEnabled !== undefined)
                this.view?.outputSpyModeEnabled(data.spyShowEnabled);
            if (data.spyShowTariff !== undefined)
                this.view?.outputSpyModeTokensPerMin(data.spyShowTariff);
        }), this.appController.sdk.broadcastManager.registerListener("onTipMenuChanged", (data) => {
            this.view?.outputTipMenu(data.tipMenuEnabled, data.tipMenu);
        }), this.appController.sdk.broadcastManager.registerListener("onPrivateShowLowBalance", (_balance) => {
            // Logger.info(`BM: low balance (${balance})`);
        }), this.appController.sdk.broadcastManager.registerListener("onSpyShowLowBalance", () => {
            //TODO DZZ DCVR-2300
        }), this.appController.sdk.broadcastManager.registerListener("onViewersCountChanged", (data) => {
            this.view?.outputViewerCount(data.count);
        }), this.appController.sdk.broadcastManager.registerListener("onTextStatusChanged", (data) => {
            this.view?.outputModelTextStatus(data.status);
        }), this.appController.sdk.broadcastManager.registerListener("onPrivateShowCompleted", (data) => {
            this.view?.outputComplimentData(data.privateRequestId, data.compliments);
            this.appController.gtm.onPrivateShowCompleted(data.privateShowFee);
        }), this.appController.sdk.broadcastManager.registerListener("onActionsChanged", (data) => {
            this.view?.outputActions(data);
        }));
    }
    _subscribeChatManager() {
        const processMessageEventData = (data) => {
            const message = {
                userId: data.fromUserId,
                clientNickname: data.fromClientNickname,
                userType: UserType[data.userType],
                message: data.message
            };
            this.view?.addMessageToChat(message);
            this._addChatItem({ message });
        };
        const showHeartsAnimation = (isHistoryData, sentByMe, amount) => {
            if (!isHistoryData && sentByMe) {
                if (amount >= globalOptions.tipsLevel1AnimationStartsAt && amount <= globalOptions.tipsLevel1AnimationEndsAt) {
                    AppView.getInstance().animationView.showAnimation("hearts3");
                }
                else if (amount >= globalOptions.tipsLevel2AnimationStartsAt && amount <= globalOptions.tipsLevel2AnimationEndsAt) {
                    AppView.getInstance().animationView.showAnimation("hearts5");
                }
                else if (amount >= globalOptions.tipsLevel3AnimationStartsAt) {
                    AppView.getInstance().animationView.showAnimation("hearts8");
                }
            }
        };
        const processTipsSentEventData = (data, isHistoryData) => {
            const tip = {
                userId: data.fromUserId,
                clientNickname: data.fromClientNickname,
                amount: data.amount
            };
            this.view?.tipHasBeenSent(tip);
            if (!isHistoryData && tip.amount >= globalOptions.tipsCoinsAnimationStartsAt) {
                AppView.getInstance().animationView.showAnimation("coins");
            }
            const authResponse = this.appController.sdk.authManager.authResponse;
            const sentByMe = authResponse !== null && authResponse.userId === data.fromUserId;
            showHeartsAnimation(isHistoryData, sentByMe, tip.amount);
            this._addChatItem({ tip });
            this.appController.gtm.tipsSent(tip.amount);
        };
        const processTipMenuPurchasedEventData = (data, isHistoryData) => {
            const tipMenuItemPurchased = {
                clientNickname: data.fromClientNickname,
                id: data.item.id,
                description: data.item.description,
                price: data.item.price,
                isPrivate: data.item.isPrivate
            };
            this.view?.tipMenuItemHasBeenRequested(tipMenuItemPurchased);
            const authResponse = this.appController.sdk.authManager.authResponse;
            const sentByMe = authResponse !== null && authResponse.userId === data.fromUserId;
            showHeartsAnimation(isHistoryData, sentByMe, tipMenuItemPurchased.price);
            this._addChatItem({ tipMenuItemPurchased });
            this.appController.gtm.tipMenuItemPurchased(tipMenuItemPurchased.price);
        };
        const processToysMenuPurchasedEventData = (data, isHistoryData) => {
            const toysMenuItemPurchased = {
                clientNickname: data.fromClientNickname,
                amount: data.itemAmount,
                title: data.itemTitle,
                vibration: data.itemVibration,
                duration: Number.parseFloat(data.itemDuration),
            };
            this.view?.toysMenuItemPurchased(toysMenuItemPurchased);
            const authResponse = this.appController.sdk.authManager.authResponse;
            const sentByMe = authResponse !== null && authResponse.userId === data.fromUserId;
            showHeartsAnimation(isHistoryData, sentByMe, toysMenuItemPurchased.amount);
            this._addChatItem({ toysMenuItemPurchased });
            this.appController.gtm.toysMenuItemPurchased(toysMenuItemPurchased.amount);
        };
        this._chatListeners.push(this.appController.sdk.chatManager.registerListener("clearChat", () => this._clearChat()), this.appController.sdk.chatManager.registerListener("deleteAllUserMessages", (args) => this._deleteAllUserMessages(args.userId)), this.appController.sdk.chatManager.registerListener("message", (data) => processMessageEventData(data)), this.appController.sdk.chatManager.registerListener("tipsSent", (data) => {
            processTipsSentEventData(data, false);
        }), this.appController.sdk.chatManager.registerListener("tipMenuItemPurchased", (data) => processTipMenuPurchasedEventData(data, false)), this.appController.sdk.chatManager.registerListener("toysMenuItemPurchased", (data) => processToysMenuPurchasedEventData(data, false)), this.appController.sdk.chatManager.registerListener("historyMessage", (data) => {
            this._clearChat();
            for (let item of data.items) {
                if (item.type === "chatMessage") {
                    processMessageEventData(item);
                }
                else if (item.type === "tipsSent") {
                    processTipsSentEventData(item, true);
                }
                else if (item.type === "tipMenuItemPurchased") {
                    processTipMenuPurchasedEventData(item, true);
                }
                else if (item.type === "toysMenuItemPurchased") {
                    processToysMenuPurchasedEventData(item, true);
                }
            }
            this._restoreChat();
        }));
    }
    _subscribeClientProfileManager() {
        this._clientProfileListeners.push(this.appController.sdk.clientProfileManager.registerListener("balanceChanged", (data) => this.view?.outputClientBalance(data.balance)));
    }
    _subscribeAll() {
        this._unsubscribeAll();
        this._subscribeAuthManager();
        this._subscribeNsManager();
        this._subscribeBroadcastManager();
        this._subscribeChatManager();
        this._subscribeClientProfileManager();
        this._subscribed = true;
    }
    _unsubscribeAll() {
        this.appController.sdk.authManager.unregisterListeners(this._authListeners);
        this.appController.sdk.nsManager.unregisterListeners(this._nsListeners);
        this.appController.sdk.broadcastManager.unregisterListeners(this._bmListeners);
        this.appController.sdk.chatManager.unregisterListeners(this._chatListeners);
        this.appController.sdk.clientProfileManager.unregisterListeners(this._clientProfileListeners);
        this._subscribed = false;
    }
    async initViewCallbacksAsync(view) {
        // await this._player?.dispose();
        this._player = this._createPlayerAsync();
        await view.initAsync(this._viewCallbacks);
    }
    async _stopPrivateOrSpyMode() {
        if (this.appController.sdk.broadcastManager === undefined)
            return;
        try {
            const isActivePrivateShow = this.isActivePrivateShow;
            const isActiveSpyMode = this.isActiveSpyMode;
            if (isActivePrivateShow) {
                await this.appController.sdk.broadcastManager.cancelPrivateShowAsync();
                this.appController.notificationCenter?.showMessage(MessageType.info, text.broadcast_menu_privateShow_privateShowStopped);
            }
            if (isActiveSpyMode && this._model) {
                await this.appController.sdk.broadcastManager.cancelSpyShowAsync();
                this.appController.notificationCenter?.showMessage(MessageType.info, StringUtils.stringFormat(text.broadcast_menu_spyMode_spyModeStopped, this._model.nickname));
            }
        }
        catch (error) {
        }
    }
    async _disposeResources() {
        this._clearChat();
        await this._stopWatchingLiveStreamAsync();
        this._broadcastId = null;
        if (this._relatedBroadcastThumbnails !== null) {
            this._relatedBroadcastThumbnails.dispose();
            this._relatedBroadcastThumbnails = null;
        }
        // await this.appController.sdk.broadcastManager.leaveRoomAsync();
        this._unsubscribeAll();
        this._bmPreviousState = BroadcastManagerState.UNINITIALIZED;
        this._bmPreviousStateChangeReason = StateChangeReason.NONE;
        // this._broadcastManager?.removeAllListeners();
        // this._broadcastManager?.dispose();
        // this._broadcastManager = undefined;
        // this._notificationService?.removeAllListeners();
        // this._notificationService?.dispose();
        // this._notificationService = undefined;
    }
    // private _forceResumeAsync = async () => {
    //     await this.resumeAsync(true);
    // }
    async _loadVideosAsync(pageIndex, pageSize) {
        if (this._broadcast === null) {
            return { totalCount: 0, pageItems: [] };
        }
        const filter = new VideoClipFilterImpl();
        filter.modelId = this._broadcast?.modelId ?? null;
        return await this.appController.sdk.galleryManager.loadVideoClipsPageAsync(pageIndex, pageSize, { filter, sorter: null, paginationCallback: null });
    }
    async _loadRecommendedVideosAsync(videoClip) {
        const filter = new VideoClipFilterImpl();
        filter.tags = videoClip.tags.map(x => x.code).join(",");
        filter.excludeId = videoClip.id;
        filter.tagFilterType = "or";
        return await this.appController.sdk.galleryManager.loadVideoClipsPageAsync(0, 10, { filter, sorter: null, paginationCallback: null });
    }
    async hideAsync() {
        this._isReady = true;
        await this._disposeResources();
        await super.hideAsync();
        // AppView.getInstance()._xrHelper?.baseExperience?.sessionManager?.session?.removeEventListener("inputsourceschange", this._forceResumeAsync);
    }
    async showAsync() {
        const appView = AppView.getInstance();
        // appView.fadingHelper?.hideScene();
        // Room announcements:
        if (!Settings.getInstance().streamTypesAnnouncement.wasViewed) {
            await appView.announcementStreamTypesPanel.prepareAsync();
        }
        //DCVR-6064
        // if (!Settings.getInstance().relatedBroadcastsAnnouncement.wasViewed) {
        //     await appView.announcementRelatedBroadcastsPanel.prepareAsync();
        // }
        await super.showAsync();
        await this.appController.environmentController.setRoomEnvironmentAsync(appView.environmentView);
        this._subscribeAll();
        this.view?.outputClientInfo(this._isGuest);
        this.view?.outputClientNickname(this._getNickname());
        // appView._xrHelper?.baseExperience?.sessionManager?.session?.addEventListener("inputsourceschange", this._forceResumeAsync);
        if (this._restartTimeout !== undefined) {
            clearTimeout(this._restartTimeout);
            this._restartTimeout = undefined;
        }
        this._isReady = true;
        // this._subscribeAuthManager();
        // const session_data = {
        //     // "xr_session_millis": 10,
        //     // "event_name": "session_data",
        //     "event": "session_data",
        //     "value": 1974
        // };
        // console.log("+++++++++++++ session_data:", session_data);
        // (window as any).dataLayer = (window as any).dataLayer || [];
        // (window as any).dataLayer.push(session_data);
        // const xrSessionDurationEventData = {
        //     "event": "xr_session_duration",
        //     "value": 1974
        // };
        // console.log("+++++++++++++ xrSessionDurationEventData:", xrSessionDurationEventData);
        // (window as any).dataLayer = (window as any).dataLayer || [];
        // (window as any).dataLayer.push(xrSessionDurationEventData);
        // appView.fadingHelper?.startFadeIn(true);
    }
    async attachViewAsync(view) {
        await super.attachViewAsync(view);
        if (!this.isVisible)
            return;
        // const broadcast = await this._reloadBroadcastAsync({broadcastId: this._broadcastId});
        await this._initView(this._broadcast, this.appController.sdk.galleryManager.videoClip);
        if (!this.isVisible)
            return;
        if (this._bmPreviousState === BroadcastManagerState.PUBLIC || this._bmPreviousState === BroadcastManagerState.MY_PRIVATE_SHOW) {
            await this._startWatchingLiveStreamAsync(this.appController.sdk.broadcastManager.currentStream);
        }
    }
    async pauseAsync() {
        await super.pauseAsync();
        await this._stopWatchingLiveStreamAsync();
        this._pauseTimeExpireHandlers();
    }
    async resumeAsync(forceRestart = false) {
        await super.resumeAsync();
        this._resumeTimeExpireHandlers();
        await this._startWatchingLiveStreamAsync(this.appController.sdk.broadcastManager.currentStream, forceRestart);
        const params = AppView.getInstance().appController.scenesSwitcher.getSitePageParams().broadcastPageParams;
        if (params?.awaitingMicrophonePermission === true) {
            params.awaitingMicrophonePermission = false;
            AppView.getInstance().appController.scenesSwitcher.setBroadcastPageParams(params);
            await this._viewCallbacks.requestAudioPrivateShowAsync();
        }
    }
    _pauseTimeExpireHandlers() {
        if (this.appController.sdk.broadcastManager !== undefined) {
            this.appController.sdk.broadcastManager.pauseTimeExpireNotifications();
        }
    }
    _resumeTimeExpireHandlers() {
        if (this.appController.sdk.broadcastManager !== undefined) {
            (async () => await this.appController.sdk.broadcastManager.resumeTimeExpireNotifications())();
        }
    }
    _getNickname() {
        return this.appController.sdk.authManager.authResponse?.clientNickname || text.broadcast_menu_unauthorizedUserNickname;
    }
    async openBroadcast(params) {
        if (this._broadcastId) {
            throw new Error("The broadcast has already been opened");
        }
        this._broadcastSwitcherState = BroadcastSwitcherState.initializing;
        try {
            await this._prepareToStart(params);
        }
        catch (e) {
            Log.error(e.message);
            this.appController.notificationCenter?.showMessage(MessageType.error, e.message);
            if (e.code === 404) {
                await this._openLobbyAsync();
            }
            return undefined;
        }
        if (this._broadcast) {
            return {
                broadcastId: this._broadcast.id,
                modelNickname: this._broadcast.modelNickname,
                videoClip: params.videoClip,
                extraOptions: params.extraOptions,
            };
        }
        return undefined;
    }
    get isActivePrivateShow() {
        return this._bmPreviousState === BroadcastManagerState.MY_PRIVATE_SHOW;
    }
    get isActiveSpyMode() {
        return this._bmPreviousState === BroadcastManagerState.SPY_SHOW;
    }
    async _prepareToStart(params) {
        this._preparingToStart = true;
        try {
            this.view?.prepareBroadcast();
            this._bmPreviousState = BroadcastManagerState.UNINITIALIZED;
            this._bmPreviousIsGuest = this.appController.sdk.broadcastManager.isGuest;
            // this._subscribeAll();
            //this._initNotificationService();
            //todo move to initialization of AudioSender
            // this.audioSender!.on(ASEvent.ON_ERROR, (data) => {
            //     Logger.error(`[AudioSender] ${data.error}: ${data.message}`);
            //     this.setAudioControlsVisibility(AudioStatus.NOT_CONNECTED);
            // });
            //todo move to initialization of AudioSender
            // this.audioSender!.on(ASEvent.ON_START, () => {
            //     this.setAudioControlsVisibility(AudioStatus.AIR);
            // });
            //todo move to initialization of AudioSender
            // this.audioSender!.on(ASEvent.ON_STOP, () => {
            //     this.setAudioControlsVisibility(AudioStatus.NOT_CONNECTED);
            // });
            // this._hasPrivateModeRequest = false;
            // await AsyncUtils.waitFor(() => this._broadcastManager);
            if (!this.isVisible) {
                return;
            }
            const lobbyPageParams = this.appController.scenesSwitcher.getSitePageParams().lobbyPageParams;
            const videoClip = params.videoClip === undefined
                ? null
                : (lobbyPageParams && lobbyPageParams.lobbyMode === "Videos" && lobbyPageParams.lobbyVideosMode === "Purchased" && params.videoClip.productId
                    ? (await this.appController.sdk.galleryManager.openVideoClipPurchaseAsync(params.videoClip.productId)).item
                    : await this.appController.sdk.galleryManager.openVideoClipAsync(params.videoClip.id));
            if (videoClip !== null) {
                params.modelNickname = videoClip.modelNickname;
            }
            else {
                this.appController.sdk.galleryManager.closeVideoClip();
                this.view?.hideVideoSpinner();
            }
            this._broadcastId = null;
            try {
                if (params.existingPrivateShow) {
                    await this.appController.sdk.broadcastManager.enterRoomAndRestorePrivateShowAsync(params.existingPrivateShow);
                    this._broadcastId = params.existingPrivateShow.broadcastId;
                    this._isCurrentPrivateAudio = params.existingPrivateShow.withClientAudioRequest;
                }
                else if (params.broadcastId) {
                    this._broadcastId = params.broadcastId;
                    await this.appController.sdk.broadcastManager.enterRoomAsync(this._broadcastId);
                    // this.tryToRestorePrivateShow();
                }
                else if (params.modelNickname) {
                    await this.appController.sdk.broadcastManager.enterRoomByModelNicknameAsync(params.modelNickname);
                    this._broadcastId = this.appController.sdk.broadcastManager.broadcastId;
                    // this.tryToRestorePrivateShow();
                }
            }
            catch (e) {
                if (e.code === 404) {
                    if (videoClip?.product?.isPurchased !== true)
                        throw e;
                }
                else {
                    throw e;
                }
            }
            this._broadcast = this.appController.sdk.broadcastManager.broadcast;
            if (videoClip) {
                this._model = { id: videoClip.modelId, nickname: videoClip.modelNickname };
            }
            else if (this._broadcast) {
                this._model = { id: this._broadcast.modelId, nickname: this._broadcast.modelNickname };
            }
            else {
                this._model = null;
            }
            this._bmPreviousState = this.appController.sdk.broadcastManager.state;
            this._bmPreviousIsGuest = this.appController.sdk.broadcastManager.isGuest;
            if (!this.isVisible) {
                return;
            }
            if (this._relatedBroadcastThumbnails !== null)
                this._relatedBroadcastThumbnails.dispose();
            this._relatedBroadcastThumbnails = new BroadcastThumbnails(BroadcastScene.RELATED_BROADCASTS_PAGE_SIZE, globalOptions.thumbnailPhotoTypeDefault, "default", {
                sdk: this.appController.sdk,
                intervalInMillis: Scene._RELOAD_INTERVAL_MILLIS,
                pageReloadMode: PageReloadMode.PAGE_BY_INDEX,
            });
            this._relatedBroadcastThumbnails?.pageItems.forEach((item) => {
                item.addListener("onBroadcastUpdated", (_args) => {
                    this.view?.outputRelatedBroadcastUpdated(item);
                    //TODO DZZ DCVR-7437 Remove!
                    // if (item.index === 0) {
                    //     setTimeout(() => {
                    //         this.view?.outputRelatedBroadcastUpdated(new BroadcastThumbnail(0, PreferredLivePhotoType.livePhoto2D, ""));
                    //     }, 15_000);
                    // }
                });
                item.addListener("onImageChanged", (args) => {
                    this.view?.outputRelatedBroadcastImageChanged(args.index, args.links);
                });
            });
            // await waitFor(() => false, 5432, { logErrorOnTimeout: false, rejectOnTimeout: false });
            await this.appController.sdk.catalogManager.loadRelatedBroadcastsAsync(this._broadcast.id, 0, BroadcastScene.RELATED_BROADCASTS_PAGE_SIZE, this._relatedBroadcastThumbnails, { showHidden: this.appController.showHiddenBroadcasts, includeFavorites: true });
            this.view?.outputRelatedBroadcastsReady();
            await this._initView(this._broadcast, videoClip);
            if (!this.isVisible) {
                return;
            }
            if (params.extraOptions?.openMessenger === true) {
                this.view?.openMessenger();
            }
            // if (params.existingPrivateShow) {
            //     await this._startWatchingLiveStreamAsync(this.appController.sdk.broadcastManager.currentStream);
            // }
            await this._startWatchingLiveStreamAsync(this.appController.sdk.broadcastManager.currentStream);
            if (!this.isVisible) {
                return;
            }
            this._broadcastSwitcherState = BroadcastSwitcherState.available;
        }
        finally {
            AppView.getInstance().fadingHelper?.startFadeIn(true);
            this._preparingToStart = false;
        }
    }
    async initAndShow() {
        try {
            await this.showAsync();
        }
        catch {
        }
        this._bmPreviousState = this.appController.sdk.broadcastManager.state;
        this._bmPreviousIsGuest = this.appController.sdk.broadcastManager.isGuest;
        this._bmPreviousStateChangeReason = StateChangeReason.NONE;
        this._subscribeAll();
        if (this._restartTimeout !== undefined) {
            clearTimeout(this._restartTimeout);
            this._restartTimeout = undefined;
        }
        this._broadcast = this.appController.sdk.broadcastManager.broadcast;
        this._broadcastId = this._broadcast?.id ?? null;
        this._isCurrentPrivateAudio = this.appController.sdk.broadcastManager.currentPrivateShowType === PrivateShowType.Voice;
        const videoClip = this.appController.sdk.galleryManager.videoClip;
        if (videoClip) {
            this._model = { id: videoClip.modelId, nickname: videoClip.modelNickname };
        }
        else if (this._broadcast) {
            this._model = { id: this._broadcast.modelId, nickname: this._broadcast.modelNickname };
        }
        else {
            this._model = null;
        }
        if (!this.isVisible)
            return;
        await this._initView(this._broadcast, this.appController.sdk.galleryManager.videoClip);
        if (!this.isVisible)
            return;
        this._broadcastSwitcherState = BroadcastSwitcherState.available;
    }
    get _isGuest() {
        return !this.appController.sdk.authManager.isAuthorized;
    }
    // private async _restartPlayerAsync() {
    //     await this._stopWatchingLiveStreamAsync();
    //     if (this._restartTimeout !== undefined)
    //     {
    //         clearTimeout(this._restartTimeout);
    //         this._restartTimeout = undefined;
    //     }
    //     this._restartTimeout = setTimeout(async () =>
    //         await this._startWatchingLiveStreamAsync(this.appController.sdk.broadcastManager.currentStream), 1000);
    // }
    async _stopWatchingLiveStreamAsync() {
        this._watching = false;
        this.view?.hidePlayer();
        this.view?.hideVideoSpinner();
        this._frequentStallsHandled = false;
        // this._playerUrl = null;
        await this._player?.stop();
        if (this._player)
            this._player.src = "";
    }
    async _startWatchingLiveStreamAsync(streamInfo, forceRestart = false) {
        if (this.appController.sdk.galleryManager.videoClip !== null) {
            return;
        }
        const url = streamInfo?.status === "online" ? (streamInfo.url ?? "") : "";
        const canPlay = url !== "";
        const differentUrl = url !== this._player?.src;
        // if (canPlay) {
        //     this._playerUrl = url;
        // }
        if (!canPlay) {
            await this._stopWatchingLiveStreamAsync();
            return;
        }
        if (forceRestart || differentUrl) {
            const lowLatency = streamInfo !== null && streamInfo.useLowLatency === true;
            if (this._player) {
                this._player.src = url;
                this._player.lowLatency = lowLatency;
            }
            await this._player?.play();
        }
        else
            this.view?.prepareProjection(url); //TODO: compare projections
        this._watching = true;
    }
    _showVideoSpinner() {
        if (this._subscribed) {
            this.view?.showVideoSpinner();
        }
    }
    _createPlayerAsync() {
        if (!this.view)
            throw new Error("BroadcastView is not initialized");
        // Player.setPlayerEngines(["MSE/MainThread"]);
        const result = this.view.videoElement;
        this._showVideoSpinner();
        result.addEventListener("statechanged", e => {
            switch (e.detail.newState) {
                case PlayerState.PREPARING:
                case PlayerState.STALLING: {
                    this._showVideoSpinner();
                    break;
                }
                case PlayerState.PLAYING: {
                    this._watching = true;
                    Logger.debug("⏵ Playing has been started.");
                    this.view?.hideVideoSpinner();
                    this.view?.showPlayer();
                    // this.view?.swapTexture("video");
                    break;
                }
                case PlayerState.STOPPED: {
                    Logger.debug("⏸ Playing has been stopped due to some reasons.");
                    if (this._watching && (this._bmPreviousState === BroadcastManagerState.PUBLIC ||
                        this._bmPreviousState === BroadcastManagerState.MY_PRIVATE_SHOW ||
                        this._bmPreviousState === BroadcastManagerState.SPY_SHOW)) {
                        if (this.appController.sdk.galleryManager.videoClip) {
                            this.view?.hideVideoSpinner();
                        }
                        else {
                            this._showVideoSpinner();
                        }
                    }
                    break;
                }
            }
        });
        // result.on(PlayerEvent.STATE_CHANGED, (args) => { // Only once!
        //     if (args.newState === PlayerState.PLAYING) {
        //         this.view?.showPlayer();
        //     }
        // });
        result.addEventListener("error", e => {
            const code = e.detail?.error;
            const message = e.detail?.message ?? "";
            if (code !== PlayerErrors.UNAUTHORIZED) {
                Logger.error(`Player [${code}]: ${message}`);
                this.appController.notificationCenter?.showMessage(MessageType.error, message);
                // await this._restartPlayerAsync();
            }
            e.stopPropagation();
        });
        result.addEventListener("warning", async (e) => {
            const code = e.detail?.error;
            const message = e.detail?.message ?? "";
            if (code === PlayerErrors.FREQUENT_STALLS) {
                if (this._frequentStallsHandled) {
                    return;
                }
                this._frequentStallsHandled = true;
                const hasMask = FeatureManager.instance.isFeatureEnabled(FeatureName.Passthrough);
                if (hasMask) {
                    FeatureManager.instance.setFeatureEnabled(FeatureName.Passthrough, false);
                    try {
                        await AppView.getInstance().appController.environmentController.setRoomEnvironmentAsync(AppView.getInstance().environmentView);
                    }
                    catch (error) {
                        Logger.error(`Failed to update environment after disabling PT: ${error}`);
                    }
                    if (this._player) {
                        this._player.videoTracks.selectedIndex = 0;
                        this._player.includeMask = false;
                    }
                    this.appController.notificationCenter?.showMessage(MessageType.error, text.common_player_error_passthrough_stopped);
                }
                else {
                    this.appController.notificationCenter?.showMessage(MessageType.error, text.common_player_error_stream_difficulties);
                }
            }
            else if (code !== PlayerErrors.UNAUTHORIZED) {
                Logger.warning(`Player [${code}]: ${message}`);
            }
        });
        result.addEventListener("invalidpassthroughtoken", async () => {
            const token = await this.appController.sdk.broadcastManager.loadPassthroughTokenAsync();
            if (token)
                result.passthroughToken = token.token;
        });
        result.addEventListener("maskdecodingerror", async () => {
            this.appController.notificationCenter?.showMessage(MessageType.error, text.common_player_error_invalid_token);
        });
        result.addEventListener("canplay", () => this.view?.prepareProjection(this._player?.src ?? ""));
        // result.on(PlayerEvent.ERROR, async (code: PlayerErrors, message: string, errorData: ErrorData) => {
        //     console.log(`%c++++ VideoClip URL update => code: ${code}, message: ${message}, errorData: ${JSON.stringify(errorData)}`, "background-color: gold");
        //     if (code === PlayerErrors.UNAUTHORIZED && this.appController.sdk.galleryManager.videoClip !== null) {
        //         const videoClipId: Nullable<number> = this.appController.sdk.galleryManager.videoClip.id;
        //         let newVideoUrl: Nullable<string> = null;
        //         try {
        //             const videoClipStreamData = await this.appController.sdk.galleryManager.reloadVideoClipStreamDataAsync(videoClipId);
        //             newVideoUrl = videoClipStreamData.url;
        //         } catch (e) {
        //             Logger.error(`Player [${code}]: ${message}`);
        //             this.appController.notificationCenter?.showMessage(MessageType.error, message);
        //         }
        //
        //         if (result.state === PlayerState.PLAYING ||
        //             result.state === PlayerState.PREPARING ||
        //             result.state === PlayerState.STALLING
        //         ) {
        //             if (newVideoUrl === null) {
        //                 await result.stop();
        //             } else {
        //                 console.log(`%c++++ ${newVideoUrl}  +++++++ ${errorData.lastTimestamp}`, "background-color: coral");
        //                 await result.play(newVideoUrl, false, errorData.lastTimestamp);
        //             }
        //         }
        //     } else {
        //         Logger.error(`Player [${code}]: ${message}`);
        //         this.appController.notificationCenter?.showMessage(MessageType.error, message);
        //     }
        // });
        // result.on(PlayerEvent.WARNING, (code: PlayerErrors, message: string) => {
        //     if (code !== PlayerErrors.UNAUTHORIZED) {
        //         Logger.warning(`Player [${code}]: ${message}`)
        //     }
        // });
        // result.on(PlayerEvent.SCREENSHOT, () => {
        //     this.view?.swapTexture("image");
        // });
        this._viewCallbacks.setPlayerSoundMuted(Settings.getInstance().broadcast.isSoundMuted);
        this._viewCallbacks.setPlayerSoundVolume(Settings.getInstance().broadcast.soundVolume);
        return result;
    }
    async _switchToPrivateOrSpyShowAsync(modelNickname) {
        if (this.appController.sdk.galleryManager.videoClip === null)
            return;
        this.appController.sdk.galleryManager.closeVideoClip();
        this.view?.outputVideoClip(null); // (stop player)
        // Switch lobby to Live-mode:
        const previousLobbyPageParams = this.appController.scenesSwitcher.getSitePageParams().lobbyPageParams;
        this.appController.scenesSwitcher.setLobbyPageParams({
            lobbyMode: "Live",
            lobbyVideosMode: previousLobbyPageParams?.lobbyVideosMode,
        });
        const broadcastPageParams = { modelNickname };
        this.appController.scenesSwitcher.setBroadcastPageParams(broadcastPageParams);
        await this.appController.scenesSwitcher.openBroadcastAsync(broadcastPageParams);
    }
    // private readonly _initVoiceToTextSDKAsync = async (onResult: (message: string) => void, onAudioChunk: (data: Float32Array) => void) => {
    //     if(this._v2tSdk)
    //     {
    //         this._v2tSdk.dispose();
    //         this._v2tSdk = undefined;
    //     }
    //     const params: VoiceToTextSDKParams = {
    //         model: Env.broadcast.menu.voiceToText.modelUrl,
    //         sampleRate: 16000,
    //         onResult,
    //         onAudioChunk
    //     }
    //     this._v2tSdk = new VoiceToTextSDK(params);
    //     const success = await this._v2tSdk.initAsync();
    //     if(!success)
    //     {
    //         this._v2tSdk.dispose();
    //         this._v2tSdk = undefined;
    //         return false;
    //     }
    //
    //     return true;
    // }
    async _openBroadcastByIdAsync(broadcastId) {
        if (this._preparingToStart)
            return;
        if (this._broadcastSwitcherState !== BroadcastSwitcherState.available)
            return;
        this._broadcastSwitcherState = BroadcastSwitcherState.requesting;
        try {
            const currentBroadcastId = this._broadcastId;
            if (this.appController.sdk.broadcastManager.broadcast && this._broadcastSwitcherState === BroadcastSwitcherState.requesting) {
                if (broadcastId !== currentBroadcastId) {
                    await this._changeBroadcastProcedure(broadcastId, null);
                }
            }
        }
        finally {
            if (this._broadcastSwitcherState === BroadcastSwitcherState.requesting) {
                this._broadcastSwitcherState = BroadcastSwitcherState.available;
            }
        }
    }
    async _changeBroadcastProcedure(broadcastId, modelNickname) {
        const currentParams = this.appController.scenesSwitcher.getSitePageParams().broadcastPageParams;
        this.appController.scenesSwitcher.setBroadcastPageParams({
            broadcastId,
            modelNickname: modelNickname ?? undefined,
            extraOptions: currentParams?.extraOptions,
        });
        await this._stopPrivateOrSpyMode();
        this._clearChat();
        await this._stopWatchingLiveStreamAsync();
        await this.appController.sdk.broadcastManager.leaveRoomAsync();
        if (this._broadcastSwitcherState === BroadcastSwitcherState.requesting) {
            this._broadcastSwitcherState = BroadcastSwitcherState.initializing;
            await this._prepareToStart({ broadcastId });
        }
    }
    async _getNeighborBroadcast(broadcastId, direction) {
        if (broadcastId === null) {
            throw new Error("Object was disposed");
        }
        const showHidden = this.appController.showHiddenBroadcasts;
        const pageParams = this.appController.scenesSwitcher.getSitePageParams();
        const openedFromPoster = pageParams.broadcastPageParams?.extraOptions?.openedFromPoster === true;
        if (openedFromPoster) {
            const filter = this.appController.sdk.catalogManager.filter;
            const categories = filter.categoriesFilter;
            filter.setFilter({
                categoryCodes: categories,
                tagCodes: "",
                streamTypeCodes: filter.streamTypesFilter,
                favoritesOnly: false,
            });
            this.appController.scenesSwitcher.setBroadcastPageParams({
                ...pageParams.broadcastPageParams,
                extraOptions: { ...(pageParams.broadcastPageParams?.extraOptions ?? {}), openedFromPoster: true },
            });
        }
        await this.appController.sdk.broadcastManager.enterNearbyRoomAsync(direction, { showHidden, includeFavorites: true });
    }
}
BroadcastScene.MAX_CHAT_HISTORY = 30;
BroadcastScene.RELATED_BROADCASTS_PAGE_SIZE = 10;
